/**
 * The light sources of the Medium tier's bake and how each one spreads over
 * the floor (lifted from the dungeon-floors prototype's `dungeon-radiance.js`):
 * point fixtures with a windowed falloff and a hot core, room stages, moon
 * pools; a per-light weight map over its bounding box with grid line of
 * sight, two dilation steps onto rock and a 5-tap binomial blur. The bake
 * (`radiance.ts`) layers these maps into its volume.
 */
import type { Floor, Room } from '@ab/game-core';
import type * as THREE from 'three';
import { presentation } from './constants.ts';

export type LightSource = {
  readonly x: number;
  readonly z: number;
  readonly height: number;
  readonly color: THREE.Color;
  readonly radius: number;
  readonly gain: number;
  readonly verticalRadius?: number;
  readonly aboveRadius?: number;
  readonly core?: number;
  readonly coreRadius?: number;
  /** A room fill: bounded by the room rectangle instead of line of sight. */
  readonly room?: Room;
  readonly stage?: { readonly axis: readonly [number, number]; readonly halfLength: number; readonly halfWidth: number };
  readonly moonPool?: readonly [number, number];
  /** Cool fills pool separately from warm fixture light. */
  readonly cool: boolean;
};

export const k = presentation.medium.bake;
const scratch = new Map<string, Float32Array>();
/** Page-lifetime grow-only scratch buffers, so each bake leaves no garbage. */
export const buffer = (name: string, n: number, fill?: number): Float32Array => {
  let a = scratch.get(name);
  if (a === undefined || a.length < n) {
    a = new Float32Array(n);
    scratch.set(name, a);
  }
  const view = a.subarray(0, n);
  if (fill !== undefined) view.fill(fill);
  return view;
};
export const at = (a: Float32Array | Uint8Array, i: number): number => a[i] ?? 0;

/** Grid line of sight in tile space: the ray may cross no wall tile before the point's own tile. */
const lineOfSight = (floor: Floor): ((su: number, sv: number, u: number, v: number) => boolean) => {
  const { width: W, height: H } = floor;
  return (su, sv, u, v) => {
    let cx = Math.floor(su);
    let cy = Math.floor(sv);
    const tx = Math.floor(u);
    const ty = Math.floor(v);
    const dx = u - su;
    const dy = v - sv;
    const stepX = dx > 0 ? 1 : -1;
    const stepY = dy > 0 ? 1 : -1;
    const tdx = dx !== 0 ? Math.abs(1 / dx) : Infinity;
    const tdy = dy !== 0 ? Math.abs(1 / dy) : Infinity;
    let tmx = dx !== 0 ? (dx > 0 ? cx + 1 - su : su - cx) * tdx : Infinity;
    let tmy = dy !== 0 ? (dy > 0 ? cy + 1 - sv : sv - cy) * tdy : Infinity;
    for (let n = 0; n < k.maxRaySteps; n += 1) {
      if (cx === tx && cy === ty) return true;
      if (tmx < tmy) {
        cx += stepX;
        tmx += tdx;
      } else {
        cy += stepY;
        tmy += tdy;
      }
      if (cx === tx && cy === ty) return true;
      if (cx < 0 || cy < 0 || cx >= W || cy >= H || floor.tiles[cy * W + cx] === 'wall') return false;
    }
    return false;
  };
};

export type Baked = LightSource & { readonly reach: number; box: readonly [number, number, number, number]; weights: Float32Array };

/** Point fixtures: windowed falloff plus the hot core; stages: a flattened band; moon pools: a soft room ellipse. */
const horizontalWeight = (t: LightSource & { reach: number }, x: number, z: number): number => {
  const dx = x - t.x;
  const dz = z - t.z;
  if (t.moonPool !== undefined) {
    const q = (dx / t.moonPool[0]) * (dx / t.moonPool[0]) + (dz / t.moonPool[1]) * (dz / t.moonPool[1]);
    return q < 1 ? (1 - q) * (1 - q) : 0;
  }
  if (t.stage === undefined) {
    const d2 = dx * dx + dz * dz;
    const r2 = t.reach * t.reach;
    if (d2 >= r2) return 0;
    const core = t.core !== undefined && t.coreRadius !== undefined ? t.core * Math.exp(-d2 / (t.coreRadius * t.coreRadius)) : 0;
    return (1 - d2 / r2) * (1 - d2 / r2) + core;
  }
  const [ax, az] = t.stage.axis;
  const along = dx * ax + dz * az;
  const across = -dx * az + dz * ax;
  const q = (along / t.stage.halfLength) * (along / t.stage.halfLength) + (across / t.stage.halfWidth) * (across / t.stage.halfWidth);
  return Math.exp(-q * q * k.stageSharpness);
};

export const verticalWeight = (t: LightSource, y: number): number => {
  const r = y > t.height ? (t.aboveRadius ?? t.verticalRadius ?? t.radius) : (t.verticalRadius ?? t.radius);
  return Math.exp(-((y - t.height) / r) * ((y - t.height) / r));
};

/** The bake's texel grid: texels per tile, size, texel centres in tile space, and which texels are open ground. */
export type TexelGrid = { readonly res: number; readonly X: number; readonly Z: number; readonly U: Float32Array; readonly V: Float32Array; readonly open: Uint8Array };

/** Every light's weight map over its own box of the texel grid. */
export const weighLights = (floor: Floor, lights: readonly LightSource[], grid: TexelGrid, reachScale: number): Baked[] => {
  const { res, X, Z, U, V, open } = grid;
  const H = floor.height;
  // Per-light weight maps over each light's box: line of sight, two dilation steps onto rock, a 5-tap binomial blur.
  const los = lineOfSight(floor);
  const sources: Baked[] = lights.map((t) => ({ ...t, reach: t.radius * reachScale, box: [0, 0, 0, 0], weights: new Float32Array(0) }));
  for (const t of sources) {
    const su = t.x + 0.5;
    const sv = -t.z + 0.5;
    const reach = t.moonPool !== undefined ? Math.max(...t.moonPool) : t.stage !== undefined ? Math.max(t.stage.halfLength, t.stage.halfWidth) * k.stageReach : t.reach;
    const room = t.room;
    const rx0 = room ? room.x : -99;
    const rx1 = room ? room.x + room.w : 1e9;
    const ry0 = room ? H - room.y - room.h : -99;
    const ry1 = room ? H - room.y : 1e9;
    const i0 = Math.max(0, Math.floor(Math.max(su - reach, rx0) * res) - 3);
    const i1 = Math.min(X - 1, Math.ceil(Math.min(su + reach, rx1) * res) + 3);
    const k0 = Math.max(0, Math.floor(Math.max(H - sv - reach, ry0) * res) - 3);
    const k1 = Math.min(Z - 1, Math.ceil(Math.min(H - sv + reach, ry1) * res) + 3);
    const bw = Math.max(0, i1 - i0 + 1);
    const bh = Math.max(0, k1 - k0 + 1);
    const w = new Float32Array(bw * bh);
    const tmp = buffer('tmp', bw * bh);
    const buf = buffer('buf', bw * bh);
    for (let z = k0; z <= k1; z += 1) {
      for (let i = i0; i <= i1; i += 1) {
        if (at(open, z * X + i) === 0) continue;
        const u = at(U, i);
        const v = at(V, z);
        if (room && !(u >= room.x && u <= room.x + room.w && v >= room.y && v <= room.y + room.h)) continue;
        const a = horizontalWeight(t, u - 0.5, 0.5 - v);
        if (a <= 0) continue;
        if (!room && !los(su, sv, u, v)) continue;
        w[(z - k0) * bw + i - i0] = t.gain * a;
      }
    }
    for (let pass = 0; pass < 2; pass += 1) {
      const src = pass === 1 ? buf : w;
      const dst = pass === 1 ? tmp : buf;
      for (let r = 0; r < bh; r += 1) {
        for (let c = 0; c < bw; c += 1) {
          const j = r * bw + c;
          dst[j] = at(src, j);
          if (at(open, (k0 + r) * X + i0 + c) === 1) continue;
          let m = at(src, j);
          const spill = k.rockSpill;
          if (c > 0) m = Math.max(m, at(src, j - 1) * spill);
          if (c < bw - 1) m = Math.max(m, at(src, j + 1) * spill);
          if (r > 0) m = Math.max(m, at(src, j - bw) * spill);
          if (r < bh - 1) m = Math.max(m, at(src, j + bw) * spill);
          dst[j] = m;
        }
      }
    }
    buf.set(tmp.subarray(0, bw * bh));
    const tap = (a: Float32Array, j: number, ok: boolean): number => (ok ? at(a, j) : 0);
    for (let r = 0; r < bh; r += 1) {
      for (let c = 0; c < bw; c += 1) {
        const j = r * bw + c;
        tmp[j] = (tap(buf, j - 2, c > 1) + 4 * tap(buf, j - 1, c > 0) + 6 * at(buf, j) + 4 * tap(buf, j + 1, c < bw - 1) + tap(buf, j + 2, c < bw - 2)) / 16;
      }
    }
    for (let r = 0; r < bh; r += 1) {
      for (let c = 0; c < bw; c += 1) {
        const j = r * bw + c;
        w[j] = (tap(tmp, j - 2 * bw, r > 1) + 4 * tap(tmp, j - bw, r > 0) + 6 * at(tmp, j) + 4 * tap(tmp, j + bw, r < bh - 1) + tap(tmp, j + 2 * bw, r < bh - 2)) / 16;
      }
    }
    t.box = [i0, k0, bw, bh];
    t.weights = w;
  }
  return sources;
};
